import React, { useEffect, useState } from "react";
import CustomModal from "components/Common/CustomModal";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { ReactComponent as Arrow } from "../../assets/img/Arrowdown.svg";
import api from "api";
import { toast } from "react-toastify";

export const inputStyle = {
    backgroundColor: "#F3F3F3",
    border: "none",
    borderRadius: "8px",
    fontFamily: "Outfit",
    width: "100%",
    padding: "12px 17px",
};

const labelStyle = {
    marginTop: "10px",
    fontWeight: 600,
    fontSize: "16px",
    fontFamily: "Outfit",
    color: "#1C1C1C",
};

const hintStyle = {
    color: "#828282",
    fontSize: "13px",
    fontWeight: 400,
    fontFamily: "Outfit",
    display: "block",
    marginTop: "4px",
};

const sampleCode = (prefix, length) => {
    const chars = "G49DM3FD2W";
    return (prefix || "") + chars.substring(0, length || 6);
};

const CreateOrder = ({ handleModal, getOrders }) => {
    const [loading, setLoading] = useState(false);
    const [brand, setBrand] = useState();
    const [allBrands, setAllBrands] = useState();
    const [batchCount, setBatchCount] = useState(1);
    const [codeType, setCodeType] = useState("regular");
    const [prefix, setPrefix] = useState("");
    const [codeLength, setCodeLength] = useState("6");
    const [externalBaseUrl, setExternalBaseUrl] = useState("");

    const getBrands = () => {
        api("get", "/users?role=brand&pageSize=100")
            .then((res) => {
                setAllBrands(res.users);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const createOrder = () => {
        if (!brand) {
            return toast.error("Please select a brand");
        }
        if (codeType === "regular" && prefix && !/^[A-Z0-9]{0,2}$/.test(prefix)) {
            return toast.error("Prefix must be 1-2 uppercase alphanumeric characters");
        }
        setLoading(true);
        const obj = {
            brandId: brand._id,
            batchIds: [],
            batchCount: batchCount,
            code_type: codeType,
            prefix: codeType === "regular" ? prefix : "",
            code_length: codeType === "regular" ? codeLength : "16",
            externalBaseUrl: (codeType === "nfc" || codeType === "codeless") ? externalBaseUrl : "",
        };
        api("post", `/orders`, obj)
            .then((res) => {
                setLoading(false);
                handleModal();
                toast.success(res.message || `Order created with ${batchCount} blank batch${batchCount > 1 ? 'es' : ''}`);
                getOrders();
            })
            .catch((err) => {
                setLoading(false);
                toast.error(err?.response?.data?.message || "Failed to create order");
            });
    };

    const handleChangeBrand = (e) => {
        setBrand(e.target.value);
    };

    const handlePrefixChange = (e) => {
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        if (value.length <= 2) {
            setPrefix(value);
        }
    };

    useEffect(() => {
        getBrands();
    }, []);

    return (
        <CustomModal
            handleSave={createOrder}
            heading="Create Order"
            buttonName="Create"
            handleModal={handleModal}
            loading={loading}
        >
            <label className="vs-label" style={{ marginTop: "10px" }}>
                Brand Name
            </label>
            <br />
            <Select
                value={brand}
                onChange={handleChangeBrand}
                renderValue={() => (brand ? brand.name : "Select a brand")}
                displayEmpty
                sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                    },
                    "& .MuiSelect-select": inputStyle,
                }}
                fullWidth
                IconComponent={(props) => <Arrow {...props} />}
            >
                {allBrands?.map((item) => {
                    return (
                        <MenuItem key={item._id} value={item}>
                            {item.name}
                        </MenuItem>
                    );
                })}
            </Select>
            <label className="vs-label" style={{ marginTop: "10px" }}>
                Number of Batches
            </label>
            <br />
            <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                <input
                    type="number"
                    min={1}
                    max={20}
                    value={batchCount}
                    onChange={(e) => {
                        const val = Math.min(20, Math.max(1, parseInt(e.target.value) || 1));
                        setBatchCount(val);
                    }}
                    style={{ ...inputStyle, width: '100px', textAlign: 'center' }}
                />
                <span style={{ fontSize: '13px', color: '#888', fontFamily: 'Outfit' }}>
                    max 20 — blank batches will be created for you to fill in
                </span>
            </div>

            {/* ── Code Type ── */}
            <label style={labelStyle}>
                Code Type
                <span style={hintStyle}>Determines how codes are generated and scanned for all batches in this order</span>
            </label>
            <div className="d-flex" style={{ gap: "8px", marginTop: "6px" }}>
                {[
                    { value: "regular", label: "Regular", tip: "QR codes with custom prefix and length" },
                    { value: "nfc", label: "NFC", tip: "16-char codes for NFC chip writing" },
                    { value: "codeless", label: "Codeless", tip: "16-char codes linked to external URL" },
                ].map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        title={opt.tip}
                        onClick={() => setCodeType(opt.value)}
                        className={codeType === opt.value ? "vs-btn-save" : "vs-btn-cancel"}
                        style={{ width: "auto", padding: "8px 18px", fontSize: "13px" }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* ── Regular code settings (prefix + length) ── */}
            {codeType === "regular" && (
                <div style={{ marginTop: "12px", padding: "12px", backgroundColor: "#f9f9fb", borderRadius: "8px" }}>
                    <div className="d-flex" style={{ gap: "12px" }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ ...labelStyle, marginTop: 0 }}>
                                Prefix
                                <span style={hintStyle}>1-2 uppercase letters/numbers prepended to each code</span>
                            </label>
                            <input
                                onChange={handlePrefixChange}
                                value={prefix}
                                placeholder="e.g. VS"
                                style={inputStyle}
                                type="text"
                                maxLength={2}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ ...labelStyle, marginTop: 0 }}>
                                Code Length
                                <span style={hintStyle}>Character count per code (excluding prefix)</span>
                            </label>
                            <select
                                value={codeLength}
                                onChange={(e) => setCodeLength(e.target.value)}
                                style={{ ...inputStyle, appearance: "auto" }}
                            >
                                <option value="6">6</option>
                                <option value="7">7</option>
                                <option value="8">8</option>
                                <option value="9">9</option>
                                <option value="10">10</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ marginTop: "8px", fontFamily: "Outfit", fontSize: "14px", color: "#555" }}>
                        Sample Code: <strong style={{ color: "#362FD8", letterSpacing: "1px" }}>
                            {sampleCode(prefix, parseInt(codeLength))}
                        </strong>
                    </div>
                </div>
            )}

            {/* ── External Base URL (NFC / Codeless) ── */}
            {(codeType === "nfc" || codeType === "codeless") && (
                <>
                    <label style={labelStyle}>
                        External Base URL
                        <span style={hintStyle}>
                            {codeType === "nfc"
                                ? "URL written to NFC chips — code is appended as path"
                                : "Base URL for codeless verification — code appended automatically"}
                        </span>
                    </label>
                    <input
                        onChange={(e) => setExternalBaseUrl(e.target.value)}
                        value={externalBaseUrl}
                        placeholder="https://yourdomain.com/verify"
                        style={inputStyle}
                        type="text"
                    />
                    <div style={{ marginTop: "8px", fontFamily: "Outfit", fontSize: "13px", color: "#828282" }}>
                        <span>Each code will get a unique link like: </span>
                        <strong style={{ color: "#362FD8", wordBreak: "break-all" }}>
                            {(externalBaseUrl || "yourdomain.com/verify").replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') + "/?h=A3F8D2K9M1"}
                        </strong>
                    </div>
                </>
            )}
        </CustomModal>
    );
};

export default CreateOrder;
